/**
 * Neuroscience derivation functions.
 * Ported from HumanSpace App frontend/src/utils/neuroscience.js
 *
 * All computed client-side from assessment results.
 */

const ACTIVATING_WEIGHTS = {
  movement: 0.30,
  enrichment: 0.35,
  daylight: 0.20,
  nature: 0.15,
};

const INHIBITING_WEIGHTS = {
  air_quality: 0.30,
  noise: 0.25,
  visual_stress: 0.15,
  acute_stress: 0.30,
};

function clamp01(v) {
  return Math.max(0, Math.min(1, v));
}

export function computeActivating(pathwayScores) {
  let total = 0;
  for (const [key, weight] of Object.entries(ACTIVATING_WEIGHTS)) {
    total += (pathwayScores[key] ?? 0) * weight;
  }
  return Math.max(0, total);
}

export function computeInhibiting(pathwayScores) {
  let total = 0;
  for (const [key, weight] of Object.entries(INHIBITING_WEIGHTS)) {
    total += (pathwayScores[key] ?? 0) * weight;
  }
  return Math.min(0, total);
}

export function computeSynergy(pathwayScores) {
  const mov = pathwayScores.movement ?? 0;
  const enr = pathwayScores.enrichment ?? 0;
  const nat = pathwayScores.nature ?? 0;

  let synergy = 0;
  if (mov > 0 && enr > 0) synergy += 0.15 * Math.min(mov, enr);
  if (nat > 0 && enr > 0) synergy += 0.10 * Math.min(nat, enr);
  return synergy;
}

export function computeBdnfBreakdown(pathwayScores) {
  const activating = computeActivating(pathwayScores);
  const inhibiting = computeInhibiting(pathwayScores);
  const synergy = computeSynergy(pathwayScores);
  const netCreb = activating + inhibiting + synergy;
  return { activating, inhibiting, synergy, netCreb };
}

export function computePathwayContributions(pathwayScores) {
  const allWeights = {
    movement: 0.25,
    enrichment: 0.25,
    daylight: 0.15,
    nature: 0.12,
    wayfinding: 0.08,
    noise: 0.05,
    air_quality: 0.05,
    visual_stress: 0.05,
  };

  return [
    'movement', 'enrichment', 'daylight', 'nature', 'wayfinding',
    'noise', 'air_quality', 'visual_stress',
  ].map(key => {
    const raw = pathwayScores[key] ?? 0;
    const contribution = raw * (allWeights[key] ?? 0.1);
    return { key, contribution };
  });
}

export function computeTemporalOutcomes(pathwayScores) {
  const mov = pathwayScores.movement ?? 0;
  const day = pathwayScores.daylight ?? 0;
  const nat = pathwayScores.nature ?? 0;
  const enr = pathwayScores.enrichment ?? 0;
  const air = pathwayScores.air_quality ?? 0;
  const noise = pathwayScores.noise ?? 0;

  const immediate = clamp01(0.40 * mov + 0.30 * day + 0.15 * enr + 0.15 * (1 + noise));
  const shortTerm = clamp01(0.30 * nat + 0.25 * (1 + air) + 0.25 * (1 + noise) + 0.20 * enr);
  const longTerm = clamp01(0.35 * enr + 0.30 * mov + 0.20 * nat + 0.15 * day);

  return {
    immediate: Math.round(immediate * 100),
    shortTerm: Math.round(shortTerm * 100),
    longTerm: Math.round(longTerm * 100),
  };
}

export function classifyNetEffect(score) {
  if (score >= 0.60) return { label: 'Strongly Positive', color: '#10b981', arrow: 'up' };
  if (score >= 0.45) return { label: 'Positive', color: '#22c55e', arrow: 'up' };
  if (score >= 0.30) return { label: 'Marginal', color: '#f59e0b', arrow: 'flat' };
  return { label: 'Negative', color: '#f43f5e', arrow: 'down' };
}

export function netEffectDescription(classification) {
  switch (classification.label) {
    case 'Strongly Positive':
      return 'This environment strongly promotes BDNF expression and hippocampal neuroplasticity';
    case 'Positive':
      return 'This environment promotes BDNF expression and supports brain health outcomes';
    case 'Marginal':
      return 'This environment has limited positive impact on BDNF — improvements recommended';
    default:
      return 'This environment may inhibit BDNF expression — interventions needed';
  }
}
